import { PlayerProfileDoc } from '@/features/player/components/types';
import { fetchPlayerProfile } from '@/features/player/queries/get-player-profile';
import { playerKeys } from '@/features/player/queries/query-keys';
import { PlayerServices } from '@/features/player/services';
import { mapPlayersLookupResponse } from '@/features/player/utils/map-player-lookup';
import { ApiError, PaginatedResponse } from '@/libs/api';
import { createPlayerCollection, updatePlayerCollection } from '@/libs/firebase/firestore';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

export function usePlayerProfileQuery(playerId?: string) {
  return useQuery({
    queryKey: playerKeys.profile(playerId),
    queryFn: () => fetchPlayerProfile(playerId as string),
    enabled: Boolean(playerId),
  });
}

export const useListPlayersQuery = (
  request: player.SortFilterSearchPlayersRequest,
): UseQueryResult<PaginatedResponse<player.PlayerLookupData>, ApiError> => {
  return useQuery<
    player.PlayersLookupResponse,
    ApiError,
    PaginatedResponse<player.PlayerLookupData>
  >({
    queryKey: playerKeys.list(request),
    queryFn: () => PlayerServices.sortFilterSearchPlayers(request),
    enabled: !!request.academyId,
    select: data => {
      const rows = mapPlayersLookupResponse(data);
      const numberOfPages = Math.ceil(
        (rows[0]?.totalQueryResults ?? 0) / (request.resultsPerPage ?? 20),
      );
      return {
        data: rows,
        meta: {
          page: request.expectedPage ?? 1,
          limit: request.resultsPerPage ?? 20,
          total: rows[0]?.totalQueryResults ?? 0,
          totalPages: numberOfPages,
        },
      };
    },
    placeholderData: previousData => previousData,
  });
};

export const useCreatePlayerMutation = () => {
  return useMutation({
    mutationFn: (player: PlayerProfileDoc) => createPlayerCollection(player, player.id),
  });
};

export const useUpdatePlayerMutation = (playerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PlayerProfileDoc>) => updatePlayerCollection(playerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerKeys.profile(playerId) });
    },
  });
};
